import { VNZAuthTokenEntity } from "~/interfaces/entities/VNZAuthTokenEntity"
import { VNZAuthTokenForLocalStorageEntity } from "~/interfaces/entities/VNZAuthTokenForLocalStorageEntity"
import { VNZESearchEntity } from "~/interfaces/entities/VNZESearch"
import { VNZImageEntity } from "~/interfaces/entities/VNZImage"
import { VNZSessionImage } from "~/interfaces/entities/VNZSessionImage"
import { Files } from "~/services/Files"
import { PrinticularApi } from "~/services/PrinticularApi"

export class VNZService {
  private authorizationUrl: string
  private baseUrl: string
  private imageProxyUrl: string
  private printicularApi: PrinticularApi

  constructor(
    baseUrl: string,
    authorizationUrl: string,
    imageProxyUrl: string,
    printicularApi: PrinticularApi
  ) {
    this.baseUrl = baseUrl.replace(/\/+$/, "")
    this.authorizationUrl = authorizationUrl.replace(/\/+$/, "")
    this.imageProxyUrl = imageProxyUrl.replace(/\/+$/, "")
    this.printicularApi = printicularApi
  }

  public getAuthenticationUrl(clientId: number, deviceToken: string) {
    const state = encodeURIComponent(
      JSON.stringify({
        deviceToken,
        clientId,
        mode: "popup",
      })
    )
    const url = `${this.authorizationUrl}&state=${state}`

    return url
  }

  public async getAccessToken(authorizationCode: string) {
    return this.printicularApi.getVerizonAccessToken(authorizationCode)
  }

  public async esearch(accessToken: string, count?: number, from?: number) {
    const queryParams = new URLSearchParams()
    queryParams.append("categories", "photo")
    queryParams.append("query", "photo_mime_type:image")

    if (count) {
      queryParams.append("count", count.toString())
    }
    if (from) {
      queryParams.append("from", from.toString())
    }

    const result = await fetch(
      `${this.baseUrl}/vzcloud-apis/v1/esearch?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    const json = await result.json()
    const data: {
      categories: string
      code: number
      total: number
      items: VNZESearchEntity[]
    } = json[0]

    return data
  }

  public async fetchPageOfImages(
    accessToken: string,
    pageSize?: number,
    page?: number
  ) {
    const pageResults = await this.esearch(accessToken, pageSize, page)

    const images = pageResults.items.map(file => {
      const filePath = (file.repoPaths[0] || "").replace(":/", "/")
      const name = filePath.split("/").pop() || file.contentToken

      return {
        ...file,
        id: file.contentToken,
        name,
        // fetchFullImage needs a name and mime type to detect and convert HEIC
        systemAttributes: { MimeType: file.mimeType },
        isLoadingThumbnail: false,
        hasLoadedThumbnail: false,
        thumbnailUrl: `${this.baseUrl}/vzcloud-apis/v1/thumbnails/${file.contentToken}?size=l`,
        isLoadingFullImage: false,
        hasLoadedFullImage: false,
        fullImageUrl: `${this.imageProxyUrl}/vzcloud-apis/v1/files/${filePath}`,
      }
    })

    return images
  }

  public async fetchThumbnail(accessToken: string, image: VNZImageEntity) {
    const imageResult = await fetch(image.thumbnailUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!imageResult.ok) {
      throw new Error(
        `Failed to fetch thumbnail. Status: ${
          imageResult.status
        }, Message: ${await imageResult.text()}`
      )
    }

    const arrayBuffer = await imageResult.arrayBuffer()
    const blob = new Blob([arrayBuffer], {
      type: imageResult.headers.get("content-type") || "image/*",
    })
    const url = URL.createObjectURL(blob)

    return url
  }

  public async fetchFullImage(accessToken: string, image: VNZImageEntity) {
    const imageResult = await fetch(image.fullImageUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (
      imageResult.status.toString().startsWith("4") ||
      imageResult.status.toString().startsWith("5")
    ) {
      throw new Error(
        `Failed to fetch full image. Status: ${
          imageResult.status
        }, Message: ${await imageResult.text()}`
      )
    }

    const arrayBuffer = await imageResult.arrayBuffer()
    const contentType = image.systemAttributes?.MimeType || "image/jpeg"

    // create a file from the array buffer so we can convert it if necessary
    const file = new File([arrayBuffer], image.name, { type: contentType })

    // if HEIC, convert to JPEG (no-op for other formats)
    const convertedFile = Files.isHEIC(file)
      ? await Files.convertHeicToJpeg(file)
      : file

    const url = URL.createObjectURL(convertedFile)

    return url
  }

  public async fetchFullImageFromSession(
    accessToken: string,
    image: VNZSessionImage
  ) {
    const { contentToken, path } = image
    const fileName = path.split("/").pop() || `${contentToken}.jpg`
    const fullImageUrl = `${
      this.imageProxyUrl
    }/vzcloud-apis/v1/files/${path.replace(/^\/+|\/+$/g, "")}`

    const imageExtension = path.split(".").pop()?.toLowerCase() || "jpg"
    const contentType = `image/${imageExtension}`

    const imageResult = await fetch(fullImageUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (
      imageResult.status.toString().startsWith("4") ||
      imageResult.status.toString().startsWith("5")
    ) {
      throw new Error(
        `Failed to fetch full image. Status: ${
          imageResult.status
        }, Message: ${await imageResult.text()}`
      )
    }

    const arrayBuffer = await imageResult.arrayBuffer()
    // create a file from the array buffer so we can convert it if necessary
    const file = new File([arrayBuffer], fileName, { type: contentType })

    return file
  }
}

export const VNZ_AUTH_ACCESS_TOKEN_KEY = "VNZ_auth_access_token"
export const VNZ_AUTH_REFRESH_TOKEN_KEY = "VNZ_auth_refresh_token"
export const VNZ_AUTH_EXPIRES_IN_KEY = "VNZ_auth_expires_in"
export const VNZ_AUTH_EXPIRY_DATE_KEY = "VNZ_auth_expiry_date"

export const clearVNZTokensFromStorage = () => {
  localStorage.removeItem(VNZ_AUTH_ACCESS_TOKEN_KEY)
  localStorage.removeItem(VNZ_AUTH_REFRESH_TOKEN_KEY)
  localStorage.removeItem(VNZ_AUTH_EXPIRES_IN_KEY)
  localStorage.removeItem(VNZ_AUTH_EXPIRY_DATE_KEY)
}

export const mapVNZTokenToStorageEntity = (
  token: VNZAuthTokenEntity
): VNZAuthTokenForLocalStorageEntity => {
  const { accessToken, refreshToken, expiresIn, expiryDt } = token

  return {
    accessToken,
    refreshToken,
    expiresIn,
    expiryDate: expiryDt,
  }
}

export const saveVNZTokensToStorage = (
  payload: VNZAuthTokenForLocalStorageEntity
) => {
  localStorage.setItem(VNZ_AUTH_ACCESS_TOKEN_KEY, payload.accessToken)
  localStorage.setItem(VNZ_AUTH_REFRESH_TOKEN_KEY, payload.refreshToken)
  localStorage.setItem(VNZ_AUTH_EXPIRES_IN_KEY, payload.expiresIn.toString())
  localStorage.setItem(VNZ_AUTH_EXPIRY_DATE_KEY, payload.expiryDate)
}

export const getVNZTokensFromStorage =
  (): VNZAuthTokenForLocalStorageEntity => {
    const accessToken = localStorage.getItem(VNZ_AUTH_ACCESS_TOKEN_KEY) || ""
    const refreshToken = localStorage.getItem(VNZ_AUTH_REFRESH_TOKEN_KEY) || ""
    const expiresIn = localStorage.getItem(VNZ_AUTH_EXPIRES_IN_KEY) || "0"
    const expiryDateString =
      localStorage.getItem(VNZ_AUTH_EXPIRY_DATE_KEY) || ""
    const expiryDate = expiryDateString
      ? new Date(expiryDateString)
      : new Date(0)

    return {
      accessToken,
      refreshToken,
      expiresIn: parseInt(expiresIn, 10),
      expiryDate: expiryDate.toISOString(),
    }
  }
